import { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Layout } from "@/components/layout/Layout";

/* --------------------------
   CATEGORY STRUCTURE
-------------------------- */

const collections = {
  all: {
    name: "All Collections",
    description: "Explore our complete range of curated fashion",
  },
  mens: {
    name: "Men's Wear",
    description: "Refined elegance for the modern gentleman",
    sub: ["shirt", "jeans", "tshirt", "cargo", "blazer"],
  },
  womens: {
    name: "Women's Wear",
    description: "Timeless grace for every occasion",
    sub: ["salwar", "top", "kurti", "skirt"],
  },
};

/* --------------------------
   STATIC ITEMS (NON-KURTI)
-------------------------- */

const staticItems = [
  // WOMENS SALWAR
  {
    id: 3,
    category: "womens",
    subCategory: "salwar",
    name: "Salwar Set 1",
    image: "/ladies-wear/salwar_1.jpeg",
  },
  {
    id: 4,
    category: "womens",
    subCategory: "salwar",
    name: "Salwar Set 2",
    image: "/ladies-wear/salwar_2.jpeg",
  },
];
/* --------------------------
   DYNAMIC SHIRT GENERATOR
-------------------------- */

const generateShirtItems = () => {
  const totalShirt = 56; // update if more images added
  return Array.from({ length: totalShirt }, (_, index) => ({
    id: 1500 + index,
    category: "mens",
    subCategory: "shirt",
    name: `Mens Shirt ${index + 1}`,
    image: `/mens-shirt/shirt-${index + 1}.jpeg`,
  }));
};

/* --------------------------
   DYNAMIC JEANS GENERATOR
-------------------------- */

const generateJeansItems = () => {
  const totalJeans = 15; // change if more images added
  return Array.from({ length: totalJeans }, (_, index) => ({
    id: 1700 + index,
    category: "mens",
    subCategory: "jeans",
    name: `Mens Jeans ${index + 1}`,
    image: `/jeans/jeans-${index + 1}.jpeg`,
  }));
};

/* --------------------------
   DYNAMIC KURTI GENERATOR
-------------------------- */

const generateKurtiItems = () => {
  const totalKurti = 36; // change if needed
  return Array.from({ length: totalKurti }, (_, index) => ({
    id: 1000 + index,
    category: "womens",
    subCategory: "kurti",
    name: `Kurti ${index + 1}`,
    image: `/kurti/kurti-${index + 1}.jpeg`,
  }));
};

/* --------------------------
   DYNAMIC TSHIRT GENERATOR
-------------------------- */

const generateTshirtItems = () => {
  const totalTshirt = 26;
  return Array.from({ length: totalTshirt }, (_, index) => ({
    id: 2000 + index,
    category: "mens",
    subCategory: "tshirt",
    name: `T-Shirt ${index + 1}`,
    image: `/mens-tshirt/tshirt-${index + 1}.jpeg`,
  }));
};

/* --------------------------
   DYNAMIC CARGO GENERATOR
-------------------------- */

const generateCargoItems = () => {
  const totalCargo = 9;
  return Array.from({ length: totalCargo }, (_, index) => ({
    id: 3000 + index,
    category: "mens",
    subCategory: "cargo",
    name: `Cargo ${index + 1}`,
    image: `/mens-cargo/cargo-${index + 1}.jpeg`,
  }));
};

/* --------------------------
   DYNAMIC BLAZER GENERATOR
-------------------------- */

const generateBlazerItems = () => {
  const totalBlazer = 6;
  return Array.from({ length: totalBlazer }, (_, index) => ({
    id: 4000 + index,
    category: "mens",
    subCategory: "blazer",
    name: `Blazer ${index + 1}`,
    image: `/blazer/blazer-${index + 1}.jpeg`,
  }));
};
/* --------------------------
   DYNAMIC SKIRT GENERATOR
-------------------------- */

const generateSkirtItems = () => {
  const totalSkirt = 24;
  return Array.from({ length: totalSkirt }, (_, index) => ({
    id: 5000 + index, // unique range
    category: "womens",
    subCategory: "skirt",
    name: `Skirt ${index + 1}`,
    image: `/skirt/skirt-${index + 1}.jpeg`,
  }));
};
/* --------------------------
   DYNAMIC TOP GENERATOR
-------------------------- */

const generateTopItems = () => {
  const totalTop = 30; // change based on total images
  return Array.from({ length: totalTop }, (_, index) => ({
    id: 6000 + index,
    category: "womens",
    subCategory: "top",
    name: `Top ${index + 1}`,
    image: `/top/top-${index + 1}.jpeg`,
  }));
};

const galleryItems = [
  ...staticItems,
  ...generateShirtItems(),
  ...generateJeansItems(),
  ...generateKurtiItems(),
  ...generateTshirtItems(),
  ...generateCargoItems(),
  ...generateBlazerItems(),
  ...generateSkirtItems(),
  ...generateTopItems(),
];

/* --------------------------
   COMPONENT
-------------------------- */

export const Collection = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [filteredItems, setFilteredItems] = useState(galleryItems);
  const [selectedItem, setSelectedItem] = useState(null);

  /* FILTER LOGIC */
  useEffect(() => {
    let filtered = galleryItems;

    if (activeCategory !== "all") {
      filtered = filtered.filter((item) => item.category === activeCategory);
    }

    if (activeSubCategory) {
      filtered = filtered.filter(
        (item) => item.subCategory === activeSubCategory,
      );
    }

    setFilteredItems(filtered);
  }, [activeCategory, activeSubCategory]);

  const handleWhatsAppEnquiry = (itemName) => {
    const message = encodeURIComponent(
      `Hello, I would like to enquire about "${itemName}" from Anand Fashion.`,
    );
    window.open(`https://wa.me/919214456664?text=${message}`, "_blank");
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="pt-32 pb-16 text-center">
        <h1 className="text-3xl font-serif mb-4">
          {collections[activeCategory]?.name}
        </h1>
        <p className="text-muted-foreground">
          {collections[activeCategory]?.description}
        </p>
      </section>

      {/* CATEGORY FILTER */}
      <section className="py-6 border-b">
        <div className="flex justify-center gap-4 flex-wrap">
          {Object.keys(collections).map((key) => (
            <button
              key={key}
              onClick={() => {
                setActiveCategory(key);
                setActiveSubCategory(null);
              }}
              className={`px-4 py-2 border ${
                activeCategory === key ? "bg-black text-white" : "bg-white"
              }`}
            >
              {collections[key].name}
            </button>
          ))}
        </div>

        {/* SUB CATEGORY */}
        {collections[activeCategory]?.sub && (
          <div className="flex justify-center gap-4 mt-4 flex-wrap">
            {collections[activeCategory].sub.map((sub) => (
              <button
                key={sub}
                onClick={() => setActiveSubCategory(sub)}
                className={`px-4 py-2 border capitalize ${
                  activeSubCategory === sub ? "bg-black text-white" : "bg-white"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* GRID */}
      <section className="py-12">
        <div className="container mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="cursor-pointer group"
              onClick={() => setSelectedItem(item)}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>
              <h3 className="mt-3 font-medium text-sm text-center">
                {item.name}
              </h3>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20">No products found.</div>
        )}
      </section>

      {/* MODAL */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-3xl">
          {selectedItem && (
            <div className="grid md:grid-cols-2 gap-6">
              <img
                src={selectedItem.image}
                alt={selectedItem.name}
                className="w-full h-full object-cover"
              />

              <div>
                <DialogHeader>
                  <DialogTitle className="text-2xl">
                    {selectedItem.name}
                  </DialogTitle>
                </DialogHeader>

                <p className="mt-4 text-muted-foreground">
                  Premium quality collection available at Anand Fashion.
                </p>

                <Button
                  onClick={() => handleWhatsAppEnquiry(selectedItem.name)}
                  className="mt-6 w-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Enquire on WhatsApp
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Collection;
